import { useEffect, useState } from "react";
import { adminApi, getApiErrorMessage } from "@/lib/api";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { formatCurrency } from "@/lib/format";
import { toast } from "sonner";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { Briefcase, FileDown, AlertOctagon, AlertTriangle, Info, MessageCircle, Mail } from "lucide-react";

const COLORS = ["#0b5c46", "#0a9970", "#63b3a0", "#e8a33d", "#7a5195", "#374c80"];
const SEV = { high: [AlertOctagon, "text-red-600 bg-red-50 border-red-200"], medium: [AlertTriangle, "text-amber-700 bg-amber-50 border-amber-200"], low: [Info, "text-slate-600 bg-slate-50 border-slate-200"] };

function Overview() {
  const [an, setAn] = useState(null);
  const [insights, setInsights] = useState([]);

  useEffect(() => {
    adminApi.get("/admin/analytics").then((r) => setAn(r.data));
    adminApi.get("/admin/insights").then((r) => setInsights(r.data));
  }, []);

  if (!an) return <div className="py-10 text-center text-sm text-[var(--ink-muted)]">Computing business analytics...</div>;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        {[["Total AUM", formatCurrency(an.total_aum), "crm-total-aum"],
          ["Clients", an.clients_count, "crm-clients-count"],
          ["Active SIPs", an.active_sips, "crm-active-sips"],
          ["SIP Book / month", formatCurrency(an.sip_book_monthly), "crm-sip-book"],
          ["Pending KYC", an.pending_kyc]].map(([l, v, tid]) => (
          <Card key={l} className="glass-card border-0"><CardContent className="p-4">
            <div className="text-xs text-[var(--ink-muted)]">{l}</div>
            <div className="mt-1 font-heading text-xl text-[var(--brand)]" data-testid={tid}>{v}</div>
          </CardContent></Card>
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="border-[var(--glass-border)]">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-[var(--brand)]">AUM by Asset Class</CardTitle></CardHeader>
          <CardContent className="h-52">
            <ResponsiveContainer width="100%" height={200} minWidth={0}>
              <PieChart>
                <Pie data={an.aum_by_asset_class} dataKey="value" nameKey="name" innerRadius={42} outerRadius={65} paddingAngle={2}>
                  {an.aum_by_asset_class.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => formatCurrency(v)} />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        <Card className="border-[var(--glass-border)]">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-[var(--brand)]">AUM by AMC (top)</CardTitle></CardHeader>
          <CardContent className="space-y-2 pt-2">
            {an.aum_by_amc.map((a, i) => {
              const max = an.aum_by_amc[0]?.value || 1;
              return (
                <div key={a.name}>
                  <div className="flex justify-between text-xs"><span>{a.name}</span><span className="text-[var(--ink-muted)]">{formatCurrency(a.value)}</span></div>
                  <div className="mt-0.5 h-1.5 rounded-full bg-[var(--surface-2)]"><div className="h-1.5 rounded-full" style={{ width: `${(a.value / max) * 100}%`, background: COLORS[i % COLORS.length] }} /></div>
                </div>
              );
            })}
          </CardContent>
        </Card>
        <Card className="border-[var(--glass-border)]">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-[var(--brand)]">Gross Inflows (6M)</CardTitle></CardHeader>
          <CardContent className="h-52">
            <ResponsiveContainer width="100%" height={200} minWidth={0}>
              <BarChart data={an.monthly_inflows}>
                <XAxis dataKey="month" tick={{ fontSize: 9 }} />
                <YAxis tick={{ fontSize: 9 }} width={55} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => formatCurrency(v)} />
                <Bar dataKey="amount" fill="#0a9970" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <Card className="border-[var(--glass-border)]">
        <CardHeader className="pb-2"><CardTitle className="text-sm text-[var(--brand)]">Actionable Insights ({insights.length})</CardTitle></CardHeader>
        <CardContent className="space-y-2" data-testid="crm-insights">
          {insights.length === 0 ? <div className="text-sm text-[var(--ink-muted)]">Book is clean — no pending actions.</div> :
            insights.slice(0, 12).map((ins, i) => {
              const [Icon, cls] = SEV[ins.severity] || SEV.low;
              return (
                <div key={i} className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-xs ${cls}`}>
                  <Icon className="h-3.5 w-3.5 shrink-0" /> {ins.message}
                </div>
              );
            })}
        </CardContent>
      </Card>
    </div>
  );
}

function ClientDetailDialog({ userId, onClose }) {
  const [detail, setDetail] = useState(null);
  useEffect(() => {
    if (userId) adminApi.get(`/admin/crm/clients/${userId}`).then((r) => setDetail(r.data));
    else setDetail(null);
  }, [userId]);

  const downloadPdf = async () => {
    try {
      const resp = await adminApi.get(`/admin/reports/portfolio-review/${userId}`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(resp.data);
      a.download = `SMIFS_Portfolio_Review_${detail?.client?.client_code || userId}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Portfolio Review PDF downloaded");
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    }
  };

  const shareVia = async (channel) => {
    try {
      const r = await adminApi.post(`/admin/reports/share/${userId}`);
      const link = `${process.env.REACT_APP_BACKEND_URL}${r.data.share_path}`;
      const msg = `Dear ${r.data.client_name},\n\nYour SMIFS Portfolio Review report is ready. Download it here (link valid for 7 days):\n${link}\n\nWarm regards,\nSMIFS Ltd — Wealth Desk`;
      if (channel === "whatsapp") {
        window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
      } else {
        window.open(`mailto:${r.data.client_email || ""}?subject=${encodeURIComponent("Your SMIFS Portfolio Review Report")}&body=${encodeURIComponent(msg)}`, "_blank");
      }
      toast.success("Share link created (valid 7 days)");
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    }
  };

  return (
    <Dialog open={!!userId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-[var(--brand)]">{detail?.client?.full_name || "Client"}</DialogTitle>
          <DialogDescription className="text-xs">{detail?.client?.client_code} · {detail?.client?.email} · KYC {detail?.client?.kyc_status}</DialogDescription>
        </DialogHeader>
        {!detail ? <div className="py-8 text-center text-sm text-[var(--ink-muted)]">Loading...</div> : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-2 text-center sm:grid-cols-4">
              {[["AUM", formatCurrency(detail.portfolio.summary.current_value)],
                ["Gain", `${detail.portfolio.summary.gain_pct}%`],
                ["XIRR", detail.portfolio.summary.active_xirr != null ? `${detail.portfolio.summary.active_xirr}%` : "—"],
                ["Health", `${detail.health.score}/100`]].map(([l, v]) => (
                <div key={l} className="rounded-xl bg-[var(--surface-2)] p-2">
                  <div className="text-[10px] uppercase text-[var(--ink-muted)]">{l}</div>
                  <div className="font-heading text-base text-[var(--brand)]">{v}</div>
                </div>
              ))}
            </div>
            <div className="divide-y divide-[var(--glass-border)] rounded-xl border border-[var(--glass-border)]">
              {detail.portfolio.holdings.map((h) => (
                <div key={h.scheme_code} className="flex items-center justify-between px-3 py-2 text-xs">
                  <span className="max-w-[300px] truncate">{h.scheme_name}</span>
                  <span className="font-medium">{formatCurrency(h.current_value)} <span className={h.gain >= 0 ? "text-emerald-700" : "text-red-600"}>({h.gain_pct}%)</span></span>
                </div>
              ))}
              {detail.portfolio.holdings.length === 0 && <div className="p-3 text-xs text-[var(--ink-muted)]">No holdings yet.</div>}
            </div>
            <Button className="w-full rounded-full bg-[var(--cta)] hover:bg-[#087a56]" onClick={downloadPdf} data-testid="crm-download-pdf-button">
              <FileDown className="mr-2 h-4 w-4" /> Download Portfolio Review PDF
            </Button>
            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" className="rounded-full border-[var(--glass-border)]" onClick={() => shareVia("whatsapp")} data-testid="crm-share-whatsapp-button">
                <MessageCircle className="mr-2 h-4 w-4 text-emerald-600" /> Share on WhatsApp
              </Button>
              <Button variant="outline" className="rounded-full border-[var(--glass-border)]" onClick={() => shareVia("email")} data-testid="crm-share-email-button">
                <Mail className="mr-2 h-4 w-4 text-[var(--brand)]" /> Share via Email
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Clients() {
  const [clients, setClients] = useState([]);
  const [selected, setSelected] = useState(null);
  useEffect(() => { adminApi.get("/admin/crm/clients").then((r) => setClients(r.data)); }, []);

  return (
    <>
      <Card className="border-[var(--glass-border)]"><CardContent className="overflow-x-auto p-0">
        <table className="w-full text-sm" data-testid="crm-clients-table">
          <thead><tr className="border-b border-[var(--glass-border)] text-left text-xs text-[var(--ink-muted)]">
            <th className="p-3 font-medium">Client</th><th className="p-3 text-right font-medium">AUM</th><th className="p-3 text-right font-medium">XIRR</th><th className="p-3 text-right font-medium">SIP / month</th><th className="p-3 font-medium">KYC</th><th className="p-3 font-medium">Hygiene</th>
          </tr></thead>
          <tbody className="divide-y divide-[var(--glass-border)]">
            {clients.map((c) => (
              <tr key={c.user_id} className="cursor-pointer hover:bg-[var(--surface-2)]" onClick={() => setSelected(c.user_id)} data-testid={`crm-client-row-${c.client_code || c.user_id}`}>
                <td className="p-3"><div className="font-medium text-[var(--brand)]">{c.name}</div><div className="text-xs text-[var(--ink-muted)]">{c.client_code || "—"} · {c.email}</div></td>
                <td className="p-3 text-right font-medium">{formatCurrency(c.aum)}</td>
                <td className={`p-3 text-right ${(c.xirr ?? 0) >= 0 ? "text-emerald-700" : "text-red-600"}`}>{c.xirr != null ? `${c.xirr}%` : "—"}</td>
                <td className="p-3 text-right">{formatCurrency(c.sip_monthly)} <span className="text-xs text-[var(--ink-muted)]">({c.active_sips})</span></td>
                <td className="p-3 text-xs">{c.kyc_status}</td>
                <td className="p-3 text-xs">
                  <span className={c.has_nominee ? "text-emerald-700" : "text-red-600"}>{c.has_nominee ? "✓" : "✕"} Nominee</span>
                  <span className={`ml-2 ${c.has_fatca ? "text-emerald-700" : "text-red-600"}`}>{c.has_fatca ? "✓" : "✕"} FATCA</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent></Card>
      <ClientDetailDialog userId={selected} onClose={() => setSelected(null)} />
    </>
  );
}

export default function AdvisorDesk() {
  return (
    <AdminLayout>
      <div className="mb-6">
        <h1 className="flex items-center gap-2 font-heading text-3xl text-[var(--brand)]"><Briefcase className="h-7 w-7" /> Advisor Desk</h1>
        <p className="mt-1 text-sm text-[var(--ink-muted)]">Your book at a glance — AUM, SIP flows, client hygiene and next actions.</p>
      </div>
      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview" data-testid="crm-tab-overview">Business Overview</TabsTrigger>
          <TabsTrigger value="clients" data-testid="crm-tab-clients">Clients</TabsTrigger>
        </TabsList>
        <TabsContent value="overview"><Overview /></TabsContent>
        <TabsContent value="clients"><Clients /></TabsContent>
      </Tabs>
    </AdminLayout>
  );
}
